import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, Chip, createStyles, Group, Stack, Text} from '@mantine/core';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import SelectableCard from 'ui-library/inputs/SelectableCard';

import RoutineFieldHeader from './RoutineFieldHeader';
import {RoutineRunAs} from './RoutineTypes';

interface RoutineRunAsSelectorProps {
  value: RoutineRunAs;
  onChange: (value: RoutineRunAs) => void;
  disabled?: boolean;
  serviceAccountOptionDisabled?: boolean;
  currentRunAs?: RoutineRunAs;
  onCopyRequest: () => void;
}

const RUN_AS_CREATOR_CARD = 'creator';
const RUN_AS_SERVICE_ACCOUNT_CARD = 'service_account';

const useStyles = createStyles((theme) => ({
  disabledWrapper: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  permissionCard: {
    '& .mantine-Group-root > svg': {
      display: 'none !important',
    },
    '& label': {
      borderRadius: theme.radius.md,
      backgroundColor: theme.white,
      border: `1.5px solid ${theme.colors.gray[3]}`,
    },
    '& label:hover:not([data-checked])': {
      borderColor: theme.colors.gray[6],
    },
    '& label[data-checked]:not([data-disabled])': {
      border: `1.5px solid ${theme.colors.blue[8]}`,
    },
  },
  suggestionText: {
    color: theme.colors.gray[6],
  },
  suggestionContent: {
    flex: 1,
  },
}));

interface CreateAgentSuggestionProps extends RoutineRunAsSelectorProps {
  ifTarget: RoutineRunAs;
  noSuggestionText: string | null;
  header: string;
}

function CreateAgentSuggestion({
  currentRunAs,
  value,
  ifTarget,
  noSuggestionText,
  header,
  onCopyRequest,
}: CreateAgentSuggestionProps): JSX.Element | null {
  const {classes} = useStyles();
  if (!currentRunAs) {
    return (
      <Text mt="xs" className={classes.suggestionText}>
        {noSuggestionText}
      </Text>
    );
  }
  if (ifTarget !== value || currentRunAs === ifTarget) {
    return null;
  }
  return (
    <Group position="apart" mt="xs">
      <Box className={classes.suggestionContent}>
        <Text fw={600}>{header}</Text>
        <Text className={classes.suggestionText}>
          Editing the permissions mode of an agent is not supported. Instead, create a separate, new agent —
          you'll leave this one behind. Everything here is copied over as a starting point.
        </Text>
      </Box>
      <Button variant="secondary" onClick={onCopyRequest}>
        Create agent...
      </Button>
    </Group>
  );
}

export default function RoutineRunAsSelector(props: RoutineRunAsSelectorProps): JSX.Element {
  const {value, onChange, disabled = false, serviceAccountOptionDisabled = false} = props;
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();
  const selectedCard =
    value === RoutineRunAs.SERVICE_ACCOUNT ? RUN_AS_SERVICE_ACCOUNT_CARD : RUN_AS_CREATOR_CARD;

  return (
    <Stack spacing="xs">
      <RoutineFieldHeader title="Permissions" description="Choose which permissions the agent should have." />
      <Chip.Group
        multiple={false}
        value={selectedCard}
        onChange={(val: string) => {
          if (disabled || val === '') {
            return;
          }
          if (val === RUN_AS_SERVICE_ACCOUNT_CARD && serviceAccountOptionDisabled) {
            assert(false, 'Unexpected attempt to select service-account run-as when that option is disabled');
          }
          onChange(val === RUN_AS_SERVICE_ACCOUNT_CARD ? RoutineRunAs.SERVICE_ACCOUNT : RoutineRunAs.CREATOR);
        }}
      >
        <Stack spacing="sm" className={cx(disabled && classes.disabledWrapper)}>
          <Box className={classes.permissionCard}>
            <SelectableCard
              value={RUN_AS_CREATOR_CARD}
              title="Uses mine"
              subtitle="Agent uses your permissions when it runs."
              icon={<BenchlingIcon icon="user" />}
              iconAppearance="grape"
              disabled={disabled}
              description={
                <CreateAgentSuggestion
                  {...props}
                  ifTarget={RoutineRunAs.CREATOR}
                  noSuggestionText={null}
                  header="Remove this agent's identity?"
                />
              }
            />
          </Box>
          <Box
            className={classes.permissionCard}
            {...getTestProps({component: 'RoutineRunAsSelector', element: 'serviceAccountCard'})}
          >
            <SelectableCard
              value={RUN_AS_SERVICE_ACCOUNT_CARD}
              title="Has its own"
              subtitle="Agent uses its own permissions when it runs."
              description={
                <CreateAgentSuggestion
                  {...props}
                  ifTarget={RoutineRunAs.SERVICE_ACCOUNT}
                  noSuggestionText="This agent will need you to grant data access to work properly"
                  header="Give this agent its own identity?"
                />
              }
              icon={<BenchlingIcon icon="robot" />}
              iconAppearance="blue"
              disabled={disabled || serviceAccountOptionDisabled}
            />
          </Box>
        </Stack>
      </Chip.Group>
    </Stack>
  );
}
